import type { Session, User } from "@supabase/supabase-js"
import { create } from "zustand"
import { identifyUser, resetUser, trackEvent } from "@/lib/posthog"
import { clearSentryUser, setSentryUser } from "@/lib/sentry"
import { supabase } from "@/lib/supabase"

interface AuthState {
  user: User | null
  session: Session | null
  isLoading: boolean
  error: string | null
  setError: (error: string | null) => void
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  checkSession: () => Promise<void>
  initialize: () => () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  isLoading: true,
  error: null,

  setError: (error) => set({ error }),

  signUp: async (email, password) => {
    set({ error: null, isLoading: true })
    const { data, error } = await supabase.auth.signUp({ email, password })
    set({ isLoading: false })
    if (error) set({ error: error.message })
    if (!error && data?.user) {
      trackEvent("user_signed_up", { email: data.user.email })
    }
    return { error: error ?? null }
  },

  signIn: async (email, password) => {
    set({ error: null, isLoading: true })
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    set({ isLoading: false })
    if (error) {
      const message =
        error.message === "Invalid login credentials"
          ? "Invalid email or password. Please try again."
          : error.message
      set({ error: message })
    }
    if (!error && data?.user) {
      trackEvent("user_signed_in", { email: data.user.email })
    }
    return { error: error ?? null }
  },

  signOut: async () => {
    set({ error: null })
    await supabase.auth.signOut()
    set({ user: null, session: null })
    clearSentryUser()
    resetUser()
  },

  checkSession: async () => {
    set({ isLoading: true })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      set({
        session: session ?? null,
        user: session?.user ?? null,
        isLoading: false,
      })
      if (session?.user) {
        setSentryUser({ id: session.user.id, email: session.user.email })
        identifyUser(session.user.id, { email: session.user.email })
      } else {
        clearSentryUser()
        resetUser()
      }
    } catch (err) {
      set({ isLoading: false })
    }
  },

  initialize: () => {
    get().checkSession()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      set({
        session: session ?? null,
        user: session?.user ?? null,
      })
      if (session?.user) {
        setSentryUser({ id: session.user.id, email: session.user.email })
        identifyUser(session.user.id, { email: session.user.email })
      } else {
        clearSentryUser()
        resetUser()
      }
    })

    return () => subscription.unsubscribe()
  },
}))
